import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { CartProvider } from './contexts/CartContext';
import Header from './components/Header';
import Home from './pages/Home';
import PublicCatalog from './pages/PublicCatalog';
import AssessmentLanding from './pages/AssessmentLanding';
import Checkout from './pages/Checkout';
import Pricing from './pages/Pricing';
import Dashboard from './pages/Dashboard';
import ReportView from './pages/ReportView';
import ComparisonTool from './pages/ComparisonTool';
import RecommendationsHub from './pages/RecommendationsHub';
import CustomReportBuilder from './pages/CustomReportBuilder';
import TeamManagement from './pages/TeamManagement';
import AssessmentHistory from './pages/AssessmentHistory';
import AdminAssessments from './pages/Admin/AdminAssessments';
import UserManagement from './pages/Admin/UserManagement';
import Login from './pages/Auth/Login';
import Signup from './pages/Auth/Signup';
import supabase from './supabase/supabase';
import { usePermissions } from './hooks/usePermissions';
import './App.css';

const ProtectedRoute = ({ children, adminOnly = false }) => {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const { isInternalStaff, loading: permLoading } = usePermissions();

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setLoading(false);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
    });
    return () => subscription.unsubscribe();
  }, []);

  if (loading || permLoading) return <div className="min-h-screen bg-gray-50 flex items-center justify-center italic text-gray-400">Authenticating...</div>;
  if (!session) return <Navigate to="/login" />;
  
  if (adminOnly && !isInternalStaff()) {
    return <Navigate to="/dashboard" />;
  }

  return children;
};

function App() {
  return (
    <HelmetProvider>
      <CartProvider>
        <Router>
          <div className="min-h-screen bg-gray-50">
            <div className="no-print">
              <Header />
            </div>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/assessments" element={<PublicCatalog />} />
              <Route path="/assessment/:slug" element={<AssessmentLanding />} />
              <Route path="/checkout" element={<Checkout />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />

              {/* Protected Routes */}
              <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
              <Route path="/report/:id" element={<ProtectedRoute><ReportView /></ProtectedRoute>} />
              <Route path="/report/:id/customize" element={<ProtectedRoute><CustomReportBuilder /></ProtectedRoute>} />
              <Route path="/compare" element={<ProtectedRoute><ComparisonTool /></ProtectedRoute>} />
              <Route path="/recommendations" element={<ProtectedRoute><RecommendationsHub /></ProtectedRoute>} />
              <Route path="/team" element={<ProtectedRoute><TeamManagement /></ProtectedRoute>} />
              <Route path="/history/:title" element={<ProtectedRoute><AssessmentHistory /></ProtectedRoute>} />

              {/* Admin CMS & Staff Tools */}
              <Route path="/admin/catalog" element={<ProtectedRoute adminOnly><AdminAssessments /></ProtectedRoute>} />
              <Route path="/admin/users" element={<ProtectedRoute adminOnly><UserManagement /></ProtectedRoute>} />
            </Routes>
          </div>
        </Router>
      </CartProvider>
    </HelmetProvider>
  );
}

export default App;